import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { isNum, formatDiamonds, formatInt, dubaiCycleDay } from '../shared/format';

/**
 * Module 1 — A1.6 "Trends vs Last Month" (spec [A1.6] / Agent Daily Activities §6:
 * compare EVERY metric to Last Month + Growth Target %, flag any trending backwards).
 *
 * 8 metrics: 2 Overview (Diamonds, Recruited Creators) + 6 Key Metrics
 * (Roster Quality 3 + New Creator Health 3). Last-month values come from the
 * manager KPI archive (manager_metrics_monthly → `*_lm` columns) for the rates,
 * and `*_last_month_clear` for the volumes.
 *
 * Delta semantics:
 *   - volume metrics (Diamonds, Recruited): pace-adjusted growth vs LM — MTD is
 *     projected to end-of-month (mtd / dayFraction) before comparing to the full
 *     last month, so nothing looks "behind" just because the month is partial.
 *     Shown as a % (growth).
 *   - rate metrics (the 6 KPIs): point delta cur − lm.
 *
 * Section title/subtitle = page SectionHeader.
 */
const CompareLastMonthCard = ({ data, thresholds = {} }) => {
  const { t } = useTranslation();
  if (!data) return null;

  // Day-fraction: prefer the pace the recompute used (mtd_target / monthly_target),
  // fall back to the calendar fraction.
  const { day, total } = dubaiCycleDay();
  const moTgt = Number(data.diamonds_monthly_target_clear);
  const mtdTgt = Number(data.diamonds_mtd_target_clear);
  const fraction = isNum(moTgt) && moTgt > 0 && isNum(mtdTgt)
    ? Math.min(1, Math.max(0.01, mtdTgt / moTgt))
    : (total > 0 ? day / total : 1);

  // — Overview volume metrics (pace-adjusted growth vs LM) —
  const volumeMetrics = [
    {
      key: 'diamonds',
      label: t('tcn.m1.trends.diamonds', 'Diamonds'),
      cur: data.diamonds_mtd_actual_clear,
      lm: data.diamonds_last_month_clear,
      target: data.diamonds_monthly_target_clear,
      fmt: formatDiamonds,
    },
    {
      key: 'recruited',
      label: t('tcn.m1.trends.recruited', 'Recruited Creators'),
      cur: data.recruited_creators_mtd_actual,
      lm: data.recruited_creators_last_month,
      target: data.recruited_creators_target,
      fmt: formatInt,
    },
  ].map((m) => {
    const hasBoth = isNum(m.cur) && isNum(m.lm) && m.lm > 0;
    const projected = isNum(m.cur) && fraction > 0 ? m.cur / fraction : null;
    const growthPct = hasBoth && isNum(projected) ? ((projected - m.lm) / m.lm) * 100 : null;
    const dir = growthPct == null ? 'flat' : growthPct < 0 ? 'down' : 'up';
    return {
      ...m,
      kind: 'volume',
      hasBoth,
      dir,
      deltaText: growthPct == null ? '—' : `${growthPct > 0 ? '+' : ''}${Math.round(growthPct)}%`,
      line: `${t('tcn.m1.trends.mtdLabel', 'MTD')} ${m.fmt(m.cur)} · ${t('tcn.m1.trends.lmLabel', 'LM')} ${m.fmt(m.lm)} · ${t('tcn.m1.trends.tgtLabel', 'tgt')} ${m.fmt(m.target)}`,
    };
  });

  // — Key Metrics (point delta cur − lm) —
  const rateMetrics = [
    { key: 'valid_go_live_rate', label: t('tcn.m1.trends.validGoLive', 'Valid Go LIVE Rate'), cur: data.valid_go_live_rate, lm: data.valid_go_live_rate_lm, target: thresholds.valid_go_live_rate ?? 30 },
    { key: 'rank_up', label: t('tcn.m1.trends.rankUp', 'Rank Up Eligibility'), cur: data.rank_up_eligibility_rate_7d15h, lm: data.rank_up_eligibility_rate_7d15h_lm, target: thresholds.rank_up_eligibility_7d15h ?? 75 },
    { key: 'pk_penetration', label: t('tcn.m1.trends.pkPenetration', 'PK Penetration'), cur: data.pk_penetration, lm: data.pk_penetration_lm, target: thresholds.pk_penetration ?? 60 },
    { key: 't14', label: t('tcn.m1.trends.t14', 'T+14 Ratio'), cur: data.t14_ratio, lm: data.t14_ratio_lm, target: thresholds.t14_ratio ?? 40 },
    { key: 'go_live_15d_45h', label: t('tcn.m1.trends.goLive15d45h', '15D/45H first 30d'), cur: data.go_live_rate_15d_45h_first30, lm: data.go_live_rate_15d_45h_first30_lm, target: thresholds.go_live_rate_15d_45h_first30 ?? 24 },
    { key: 'pk20', label: t('tcn.m1.trends.pk20', 'PK ≥20 first 30d'), cur: data.first_30d_pk_count_20_ratio, lm: data.first_30d_pk_count_20_ratio_lm, target: thresholds.first_30d_pk_count_20_ratio ?? 13 },
  ].map((m) => {
    const hasBoth = isNum(m.cur) && isNum(m.lm);
    const delta = hasBoth ? Math.round(m.cur) - Math.round(m.lm) : null;
    const dir = delta == null ? 'flat' : delta < 0 ? 'down' : 'up';
    return {
      ...m,
      kind: 'rate',
      hasBoth,
      dir,
      deltaText: delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta}`,
      line: `${isNum(m.cur) ? `${Math.round(m.cur)}%` : '—'} · ${t('tcn.m1.trends.lmLabel', 'LM')} ${isNum(m.lm) ? `${Math.round(m.lm)}%` : '—'} · ${t('tcn.m1.trends.tgtLabel', 'tgt')} ${m.target}%`,
    };
  });

  const rows = [...volumeMetrics, ...rateMetrics];
  const backwards = rows.filter((r) => r.dir === 'down').length;
  const comparable = rows.filter((r) => r.hasBoth).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((r) => {
        const back = r.dir === 'down';
        const tinted = r.hasBoth && back;
        const deltaColor = !r.hasBoth ? TCN.textMute : back ? TCN.red : TCN.green;
        return (
          <div
            key={r.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              borderRadius: 12,
              background: tinted ? `${TCN.red}10` : TCN.surface1,
              border: `1px solid ${tinted ? `${TCN.red}40` : TCN.border}`,
            }}
          >
            {r.hasBoth ? (
              <Icon name={back ? 'arrow-down' : 'arrow-up'} size={16} color={back ? TCN.red : TCN.green} />
            ) : (
              <span style={{ width: 16, textAlign: 'center', color: TCN.textMute }}>·</span>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: TCN.text }}>{r.label}</div>
              <div
                style={{ fontSize: 11, color: TCN.textMute, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}
                dir="ltr"
              >
                {r.line}
              </div>
            </div>
            <span
              dir="ltr"
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: deltaColor,
                fontVariantNumeric: 'tabular-nums',
                fontFamily: TCN.fontMono,
                whiteSpace: 'nowrap',
              }}
            >
              {r.deltaText}
            </span>
          </div>
        );
      })}

      {comparable === 0 ? (
        <div style={{ marginTop: 4, padding: 12, background: TCN.surface1, borderRadius: 12, border: `1px solid ${TCN.border}`, fontSize: 12, color: TCN.textDim }}>
          {t('tcn.m1.trends.noLm', 'No last-month data to compare yet.')}
        </div>
      ) : backwards > 0 ? (
        <div style={{ marginTop: 4, padding: 12, background: `${TCN.red}10`, borderRadius: 12, border: `1px solid ${TCN.red}30`, fontSize: 12, color: TCN.text }}>
          <strong style={{ color: TCN.red }}>
            {t('tcn.m1.trends.backwardsCount', '{{count}} metrics', { count: backwards })}
          </strong>{' '}
          {t('tcn.m1.trends.backwards', 'trending backwards vs last month.')}
        </div>
      ) : (
        <div style={{ marginTop: 4, padding: 12, background: `${TCN.green}10`, borderRadius: 12, border: `1px solid ${TCN.green}30`, fontSize: 12, color: TCN.text }}>
          {t('tcn.m1.trends.allUp', 'All metrics up or flat vs last month.')}
        </div>
      )}
    </div>
  );
};

export default CompareLastMonthCard;
